import { useState } from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { ArrowLeft } from 'lucide-react'
import { ConversationList } from './conversation-list'
import { MessageThread } from './message-thread'
import { ReviewQueue } from './review-queue'
import { SectionHelp } from '@/components/help/section-help'
import { type Conversation, type InboxFilter, type InboxScope } from './use-inbox'

export function InboxPage() {
  const [filter, setFilter] = useState<InboxFilter>('all')
  const [scope, setScope] = useState<InboxScope>('all')
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null)
  const [mobileView, setMobileView] = useState<'list' | 'thread'>('list')

  function handleSelectConversation(conversation: Conversation) {
    setSelectedConversation(conversation)
    setMobileView('thread')
  }

  function handleBackToList() {
    setMobileView('list')
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col overflow-hidden">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <h1 className="text-sm font-semibold text-[var(--color-silver-bright)]">Inbox</h1>
        <SectionHelp sectionKey="inbox" />
      </div>
      {/* Review queue banner — always at top */}
      <div data-tour="inbox-review">
        <ReviewQueue />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop: always show both panels */}
        {/* Mobile: show only the active view */}

        {/* Conversation list */}
        <div
          className={cn(
            'h-full',
            // Mobile: full width when showing list, hidden when showing thread
            mobileView === 'list' ? 'flex' : 'hidden',
            // Desktop: always visible, fixed width
            'md:flex',
          )}
          data-tour="inbox-list"
        >
          <ConversationList
            filter={filter}
            onFilterChange={setFilter}
            scope={scope}
            onScopeChange={setScope}
            selectedLeadId={selectedConversation?.lead_id ?? null}
            onSelect={handleSelectConversation}
          />
        </div>

        {/* Thread panel */}
        <div
          className={cn(
            'flex flex-1 flex-col overflow-hidden',
            // Mobile: only visible when thread view active
            mobileView === 'thread' ? 'flex' : 'hidden',
            'md:flex',
          )}
          data-tour="inbox-thread"
        >
          {/* Mobile back button */}
          {selectedConversation && (
            <div className="border-b px-3 py-2 md:hidden">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 gap-1.5 text-xs"
                onClick={handleBackToList}
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Back to conversations
              </Button>
            </div>
          )}

          <MessageThread
            conversation={selectedConversation}
            className="flex-1"
          />
        </div>
      </div>
    </div>
  )
}
